import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import beforeafter from '@client/images/previews/beforeafter.png';
import { ShadowPopupWrapper } from '@client/components/common/Modal';
import { Button } from '@client/components/common/inputs/Button';
import { F1 } from '../../helpers/types';
import framePng from '@design-system/components/mobile-preview/assets/frame.png';
import { CircularProgress } from '@mui/material';

export enum TPreviewType {
  mobile = 'mobile',
  desktop = 'desktop',
}

const CloseModalIcon = ({
  onClose,
  position = 'right',
}: ModalProps & { position?: 'right' | 'left' }) => {
  const posStyles = position === 'right' ? 'right-6 top-6' : 'left-6 top-6';
  return (
    <FontAwesomeIcon
      icon={faClose}
      className={`leading-none text-sm w-[24px] h-[24px] absolute ${posStyles} cursor-pointer`}
      onClick={() => onClose(true)}
    />
  );
};

type ModalProps = {
  onClose: F1<boolean>;
};

type PreviewModalProps = ModalProps & {
  data: any;
  previewType: TPreviewType;
  previewUrl: string;
  theme: string;
};

export const PreviewModal = ({
  data,
  onClose,
  previewType,
  previewUrl,
  theme,
}: PreviewModalProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  useEffect(() => {
    if (formRef.current) formRef.current.submit();
  }, [data, theme]);

  const isMobilePreview = useMemo(
    () => previewType === TPreviewType.mobile,
    [previewType],
  );

  useEffect(() => {
    if (!isMobilePreview) return;
    setIsFrameLoaded(false);

    const handleClick = (event: MouseEvent) => {
      if (
        previewRef.current &&
        !previewRef.current.contains(event.target as Node)
      ) {
        onClose(true);
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isMobilePreview, onClose]);

  const previewWidth =
    previewType === TPreviewType.desktop
      ? 'w-[90vw]'
      : previewType === TPreviewType.mobile
        ? 'w-[375px]'
        : 'w-[90vw]';

  const closeIconPosition =
    previewType === TPreviewType.desktop ? 'right' : 'left';

  return (
    <>
      <form
        ref={formRef}
        action={previewUrl}
        method="post"
        target="preview_frame"
        className="hidden"
      >
        <input name="pageConfig" value={JSON.stringify(data)} readOnly />
        <input type="hidden" name="theme" value={theme} readOnly />
        <input
          type="checkbox"
          name="isMobile"
          checked={previewType === 'mobile'}
          hidden
        />
      </form>
      <ShadowPopupWrapper {...{ onClose }}>
        {isMobilePreview ? (
          <div className="flex min-h-screen w-full items-start justify-center px-4 pb-10 pt-[15vh]">
            <div ref={previewRef} className="relative">
              <CloseModalIcon onClose={onClose} position="right" />
              <div className="relative h-[764px] w-[374px]">
                <img
                  src={framePng}
                  alt=""
                  className="absolute inset-0 h-full w-full pointer-events-none select-none"
                />
                <div className="absolute left-[18px] right-[18px] top-[15px] bottom-[15px] overflow-hidden rounded-[46px] bg-white shadow-lg">
                  {!isFrameLoaded && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                      <CircularProgress color="info" size={48} thickness={4} />
                    </div>
                  )}
                  <iframe
                    className="h-full w-full"
                    name="preview_frame"
                    onLoad={() => setIsFrameLoaded(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${previewWidth} h-[90vh] bg-white shadow-lg`}
          >
            <CloseModalIcon onClose={onClose} position={closeIconPosition} />
            <iframe className={`w-full h-full`} name="preview_frame" />
          </div>
        )}
      </ShadowPopupWrapper>
    </>
  );
};

export const HideWarningModal = ({ onClose }: ModalProps) => {
  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div
        className={`flex flex-col justify-center items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[358px] bg-white rounded-[10px] font-[Gilroy]`}
      >
        <CloseModalIcon onClose={onClose} />
        <h1 className="text-h32 font-bold text-gray-1 mb-0">
          Hide this section?
        </h1>
        <h2 className="text-h16 font-medium text-gray-2 max-w-[287px] text-center mb-0 mt-[20px]">
          If you hide this section, it will not be displayed on your landing
          page
        </h2>
        <div className="flex space-x-[20px] mt-[40px]">
          <Button onClick={() => onClose(false)} variant="gray">
            Cancel
          </Button>
          <Button onClick={() => onClose(true)}>Okay</Button>
        </div>
      </div>
    </ShadowPopupWrapper>
  );
};

export const BeforeAfterModal = ({ onClose }: ModalProps) => {
  const header = 'Before/After Image';
  const description =
    'For best results, we recommend the following to create visually beautiful before/after photos:';

  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div className="lbp-modal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] md:w-[867px] bg-white shadow-lg p-10">
        <CloseModalIcon onClose={onClose} />
        <div className="flex items-center">
          <img
            src={beforeafter}
            alt=""
            className="flex-basis[270px] max-w-[270px] max-h-[270px]"
          />
          <div className="ml-[50px] flex-shrink flex-basis-[363px]">
            <h1 className="text-gray-1">{header}</h1>
            <p className="text-gray-2">{description}</p>
            <br />
            <ul className="text-gray-1 list-disc ml-[30px]">
              <li>Put 2 vertical photos side-by-side</li>
              <li>Avoid any dividing lines between the 2 photos</li>
              <li>Final image should be square</li>
              <li>
                See our{' '}
                <a
                  href="https://www.canva.com/design/DAFvBafZGLE/Jzl_s8ChkDTnk1KecOK7xA/view?utm_content=DAFvBafZGLE&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                  target="_blank"
                  rel="noreferrer"
                >
                  Canva
                </a>{' '}
                template
              </li>
            </ul>
          </div>
        </div>
      </div>
    </ShadowPopupWrapper>
  );
};
